import React from 'react';
import { Sequence, AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion';
import { ThemeSystemConfig } from '../../clipCreator/remotion/Components/Theme/Theme';
import { AspectRatio } from '../AspectRatio';
import { TimelineDefaults } from '../TimelineDefaults';
import { calculateGridPosition } from '../Utils';
import { BlendMode } from './style';
import { PositionedItem, PositionedItemImpl } from './positioned';
import { AnimatedItem, AnimationConfig, SoundConfig, AnimationTimingMode } from './animated';
import { AnimationHelpers } from '../helpers';
import { SequenceTimingHelper, AnimationState } from '../helpers/SequenceTimingHelper';

export interface OverlayItem extends PositionedItem, AnimatedItem {
    blendMode?: BlendMode;
    videoMuted?: boolean; // Convenience property to mute video overlays
    videoVolume?: number; // Convenience property to control video overlay volume (0-1)
}

export class OverlayItemImpl extends PositionedItemImpl implements OverlayItem {
    blendMode: BlendMode;
    videoMuted?: boolean;
    videoVolume?: number;
    // AnimatedItem properties
    entranceAnimation?: AnimationConfig;
    exitAnimation?: AnimationConfig;
    entranceSoundEffect?: SoundConfig;
    exitSoundEffect?: SoundConfig;
    customAnimations: AnimationConfig[];
    customSoundEffects: SoundConfig[];

    constructor(overlay: Omit<OverlayItem, 'render'>) {
        super(overlay);
        this.blendMode = overlay.blendMode ?? 'normal';
        this.videoMuted = overlay.videoMuted;
        this.videoVolume = overlay.videoVolume;
        
        // Apply video audio settings to the media object if it's a video
        if (overlay.mediaObject.type === 'video' && (overlay.videoMuted !== undefined || overlay.videoVolume !== undefined)) {
            // Create a new video media object with the audio settings
            const videoMedia = overlay.mediaObject as any;
            videoMedia.muted = overlay.videoMuted ?? videoMedia.muted;
            videoMedia.volume = overlay.videoVolume ?? videoMedia.volume;
        }
        
        // Initialize AnimatedItem properties
        this.entranceAnimation = overlay.entranceAnimation;
        this.exitAnimation = overlay.exitAnimation;
        this.entranceSoundEffect = overlay.entranceSoundEffect;
        this.exitSoundEffect = overlay.exitSoundEffect;
        this.customAnimations = overlay.customAnimations ?? [];
        this.customSoundEffects = overlay.customSoundEffects ?? [];
    }

    // Sound effects are handled by the timeline's SoundPlayer component
    // This method is kept for interface compatibility but is not used

    render(fps: number, theme: ThemeSystemConfig, aspectRatio: AspectRatio, defaults: TimelineDefaults, videoConfig?: { width: number; height: number }): React.ReactNode {
        // Use centralized sequence timing calculation
        const { startFrame, durationFrames } = SequenceTimingHelper.calculateSequenceTiming(
            this.startAt,
            this.duration,
            fps,
            this.entranceAnimation,
            this.exitAnimation
        );

        // UNIFIED: Use SequenceTimingHelper for consistent sequence naming
        const sequenceName = SequenceTimingHelper.createSequenceName(
            'Overlay',
            this.id,
            this.startAt,
            this.duration,
            this.entranceAnimation,
            this.exitAnimation
        );

        // Debug sequence props with corrected timing information
        console.log(`[OVERLAY_SEQUENCE_FIX] ${this.id} sequence timing after fix:`, {
            originalStartAt: `${this.startAt}s`,
            originalDuration: `${this.duration}s`,
            calculatedStartFrame: startFrame,
            calculatedDurationFrames: durationFrames,
            sequenceStartTime: `${startFrame / fps}s`,
            sequenceDuration: `${durationFrames / fps}s`,
            hasEntranceAnimation: !!this.entranceAnimation,
            hasExitAnimation: !!this.exitAnimation,
            entranceAnimationDuration: this.entranceAnimation?.timing.duration || 0,
            exitAnimationDuration: this.exitAnimation?.timing.duration || 0,
            'EXPECTED_BEHAVIOR': `Sequence starts at ${startFrame / fps}s, item visible at ${this.startAt}s`
        });

        const sequenceProps = {
            key: this.id,
            name: sequenceName,
            from: startFrame,
            durationInFrames: durationFrames
        };
        
        // CRITICAL DEBUG: Verify sequence props are correct
        console.log(`[SEQUENCE_PROPS_VERIFICATION] ${this.id}:`, {
            calculatedStartFrame: startFrame,
            calculatedDurationFrames: durationFrames,
            'EXPECTED_BEHAVIOR': {
                sequenceStartsAt: `${startFrame / fps}s (frame ${startFrame})`,
                overlayVisibleAt: `${this.startAt}s (frame ${this.startAt * fps})`,
                sequenceEndsAt: `${(startFrame + durationFrames) / fps}s (frame ${startFrame + durationFrames})`
            },
            'ENTRANCE_ANIMATION': {
                duration: this.entranceAnimation?.timing.duration || 0,
                'startsAt': `${(startFrame / fps).toFixed(2)}s`,
                'completesAt': `${this.startAt}s`
            }
        });

        const position = calculateGridPosition(
            this.gridArea,
            this.scale,
            this.layoutPadding ?? defaults?.layoutPadding ?? 0.05,
            this.paddingMode ?? 'container',
            this.position
        );

        return (
            <DynamicOverlaySequence
                overlay={this}
                sequenceProps={sequenceProps}
                position={position}
                fps={fps}
                theme={theme}
                aspectRatio={aspectRatio}
                defaults={defaults}
                videoConfig={videoConfig}
            />
        );
    }
}


// Note: AnimationState and calculateAnimationWindow moved to SequenceTimingHelper
// for unified timing system

/**
 * DynamicOverlaySequence wrapper that changes its name based on current animation state
 * REFACTORED: Now uses unified SequenceTimingHelper for consistent timing and state management
 */
const DynamicOverlaySequence: React.FC<{
    overlay: OverlayItemImpl;
    sequenceProps: any;
    position: React.CSSProperties;
    fps: number;
    theme: ThemeSystemConfig;
    aspectRatio: AspectRatio;
    defaults: TimelineDefaults;
    videoConfig?: { width: number; height: number };
}> = ({ overlay, sequenceProps, position, fps, theme, aspectRatio, defaults, videoConfig }) => {
    const currentFrame = useCurrentFrame();
    const remotionVideoConfig = useVideoConfig();
    const consistentFps = remotionVideoConfig.fps;
    
    // UNIFIED: Get animation state from SequenceTimingHelper
    // This provides both the animation execution and state information in one call
    const animationResult = SequenceTimingHelper.getSequenceAnimationStyles(
        currentFrame,
        fps,
        overlay.duration,
        overlay.entranceAnimation,
        overlay.exitAnimation,
        undefined, // baseTransforms
        overlay.opacity ?? 1
    );
    
    // SIMPLIFIED: Use the same static naming as the render method for consistency
    const sequenceName = SequenceTimingHelper.createSequenceName(
        'Overlay',
        overlay.id,
        overlay.startAt,
        overlay.duration,
        overlay.entranceAnimation,
        overlay.exitAnimation
    );
    
    // Update sequence props with static name
    const dynamicSequenceProps = {
        ...sequenceProps,
        name: sequenceName
    };
    
    // Simplified debug output
    console.log(`[OVERLAY_SEQUENCE] ${overlay.id}: ${sequenceName}`);
    
    return (
        <Sequence {...dynamicSequenceProps}>
            <AbsoluteFill>
                <OverlayItemContent
                    overlay={overlay}
                    position={position}
                    fps={fps}
                    theme={theme}
                    aspectRatio={aspectRatio}
                    defaults={defaults}
                    videoConfig={videoConfig}
                />
            </AbsoluteFill>
        </Sequence>
    );
};

/**
 * OverlayItemContent component handles animation integration within Remotion context
 */
const OverlayItemContent: React.FC<{
    overlay: OverlayItemImpl;
    position: React.CSSProperties;
    fps: number;
    theme: ThemeSystemConfig;
    aspectRatio: AspectRatio;
    defaults: TimelineDefaults;
    videoConfig?: { width: number; height: number };
}> = ({ overlay, position, fps, videoConfig }) => {
    const currentFrame = useCurrentFrame();
    
    // SIMPLIFIED: Use unified SequenceTimingHelper for consistent animation execution
    // This provides the same timing calculations as DynamicOverlaySequence uses for naming
    const animationResult = SequenceTimingHelper.getSequenceAnimationStyles(
        currentFrame,
        fps,
        overlay.duration,
        overlay.entranceAnimation,
        overlay.exitAnimation,
        undefined, // baseTransforms
        overlay.opacity ?? 1 // default opacity for non-animated overlays
    );
    
    const { animationStyles, visibility, animationState } = animationResult;
    
    // Simplified debug logging - detailed timing handled by SequenceTimingHelper
    console.log(`[OVERLAY_CONTENT_UNIFIED] ${overlay.id} at frame ${currentFrame}: ${animationState}`);
    
    // Sound effects are handled by the timeline's SoundPlayer component
    // No need to trigger sounds here as they're managed centrally
    
    // If fully inactive (pre-roll before entrance or after exit), render nothing to avoid stacking DOM nodes
    if (animationState === AnimationState.INACTIVE) {
        return null;
    }

    // DEBUG: Log what animationStyles contains
    console.log(`[OVERLAY_DEBUG] ${overlay.id} animationStyles:`, animationStyles);
    
    // FIXED: Extract CSS animation classes from animation styles
    // The CSS animation system returns special properties that need to be applied as classes
    const cssAnimationClass = (animationStyles as any).animationClass;
    const cssAnimationPrefix = (animationStyles as any).animationPrefix || 'animate__';
    
    console.log(`[OVERLAY_DEBUG] ${overlay.id} CSS extraction:`, {
        cssAnimationClass,
        cssAnimationPrefix,
        hasClass: !!cssAnimationClass
    });
    
    // Create clean CSS properties without the special class properties
    const cleanAnimationStyles = { ...animationStyles };
    delete (cleanAnimationStyles as any).animationClass;
    delete (cleanAnimationStyles as any).animationName;
    delete (cleanAnimationStyles as any).animationPrefix;
    
    // Combine opacity properly - visibility should only override if explicitly set
    const baseStyle = {
        position: 'absolute' as const,
        ...position,
        opacity: overlay.opacity,
        mixBlendMode: overlay.blendMode,
        // Stabilize stacking order so newer overlays are always on top
        zIndex: Math.max(0, Math.floor((overlay.startAt ?? 0) * 1000)),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
    };
    
    // Apply animation styles (which may include opacity from fade animations)
    const styleWithAnimations = { ...baseStyle, ...cleanAnimationStyles };
    
    // Only apply visibility opacity if it's explicitly hiding the element
    if (visibility.opacity === 0) {
        styleWithAnimations.opacity = 0;
    }
    
    // Build className for CSS animations
    let className = '';
    if (cssAnimationClass) {
        // Apply the base animate.css class and the specific animation class
        className = `${cssAnimationPrefix}animated ${cssAnimationClass}`;
        console.log(`[CSS_ANIMATION_FIX] ${overlay.id}: Applying CSS classes: ${className}`);
        
        // DEBUG: Verify frame-seeking properties are applied
        if (styleWithAnimations.animationPlayState === 'paused') {
            console.log(`[CSS_FRAME_SEEKING_APPLIED] ${overlay.id}:`, {
                animationPlayState: styleWithAnimations.animationPlayState,
                animationDelay: styleWithAnimations.animationDelay,
                animationDuration: styleWithAnimations.animationDuration,
                animationFillMode: styleWithAnimations.animationFillMode
            });
        } else {
            console.warn(`[CSS_FRAME_SEEKING_MISSING] ${overlay.id}: Frame-seeking properties not found in styles`);
        }
    }
    
    return (
        <div 
            style={styleWithAnimations} 
            className={className}
        >
            {overlay.mediaObject.render(fps, overlay.id, overlay.duration ?? 0, overlay.objectFit, videoConfig)}
        </div>
    );
};